
// src/components/Navbar.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import AnnouncementBar from "./AnnouncementBar.jsx";
import { useCart } from "../contexts/CartContext";
import { useLogin } from "../contexts/LoginContext";
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, getUserRecentSearches, saveUserRecentSearches } from '../firebase/firestore';

const logo = "/assets/logo1.png";

/* Icons (inline, no deps) */
const IconSearch = (p) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-3.5-3.5" />
  </svg>
);
const IconClose = (p) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <path d="M6 6l12 12M18 6l-12 12" />
  </svg>
);
const IconTrend = (p) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M14 8h6v6" />
  </svg>
);
const IconHeart = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconPhone = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="18" x2="12.01" y2="18" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconUser = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <circle cx="12" cy="8" r="4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 21c0-2.5-2-4.5-4.5-4.5h-7C6 16.5 4 18.5 4 21" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconCart = (p) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...p}>
    <circle cx="9" cy="21" r="1" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="20" cy="21" r="1" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


/* Typing placeholder phrases */
const SEARCH_PHRASES = [
  "Search sarees, lehengas, kurtas",
  "Kanchipuram silk sarees",
  "Banarasi sarees",
  "Uppada soft silk",
  "Wedding collection",
];

/* Popular search terms */
const POPULAR_SEARCHES = [
  "Kanchipuram Sarees",
  "Banarasi Silk",
  "Wedding Collection",
  "Festival Wear",
  "Designer Sarees",
  "Handloom Sarees",
  "Silk Lehengas",
  "Traditional Wear"
];

/* --- Use Firebase Auth instead of localStorage --- */
/* ----------------------------------- */

export default function Navbar() {
  const [q, setQ] = useState("");
  const [openSearch, setOpenSearch] = useState(false);
  const { openAuth, setOpenAuth, setAuthMode, setPendingDest } = useLogin();
  const [atTop, setAtTop] = useState(true);
  const { currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [toast, setToast] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Initialize isMobile immediately to prevent flash on desktop
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768; // Use 768px for proper mobile detection
    }
    return false;
  });
  const [recentSearches, setRecentSearches] = useState([]);

  // Load user data from Firestore when currentUser changes
  const loadUserData = useCallback(async () => {
    if (currentUser) {
      try {
        const userProfile = await getUserProfile(currentUser.uid);
        if (userProfile) {
          setUser({
            ...currentUser,
            ...userProfile,
            name: userProfile.name || userProfile.displayName || currentUser.displayName
          });
        } else {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        setUser(currentUser);
      }
    } else {
      setUser(null);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserData();

    // Listen for custom user login event (for backward compatibility)
    const handleUserLogin = (e) => {
      if (e.detail) {
        setUser(e.detail);
      }
    };

    window.addEventListener('userLogin', handleUserLogin);

    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
    };
  }, [loadUserData]);

  // Get cart and wishlist counts from context
  const { getCartCount, getWishlistCount } = useCart();
  const cartCount = getCartCount();
  const wishCount = getWishlistCount();

  const [typedPh, setTypedPh] = useState(""); // animated placeholder

  const inputRef = useRef(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const allowTransparent = pathname === "/";
  const isAuthed = !!user;

  useEffect(() => { if (openSearch && inputRef.current) inputRef.current.focus(); }, [openSearch]);

  // Load recent searches from Firestore when search opens
  useEffect(() => {
    if (openSearch && currentUser) {
      getUserRecentSearches(currentUser.uid).then(searches => {
        setRecentSearches(searches);
      }).catch(() => {
        setRecentSearches([]);
      });
    } else if (openSearch) {
      setRecentSearches([]); // No user, no saved searches
    }
  }, [openSearch, currentUser]);

  // animate placeholder
  useEffect(() => {
    if (!openSearch) return;
    let t; const state = { i: 0, j: 0, mode: "type" };
    const step = () => {
      const phrase = SEARCH_PHRASES[state.i];
      if (state.mode === "type") {
        setTypedPh(phrase.slice(0, state.j)); state.j += 1;
        if (state.j > phrase.length) { state.mode = "pause"; t = setTimeout(() => { state.mode = "erase"; step(); }, 1100); return; }
        t = setTimeout(step, 40);
      } else if (state.mode === "erase") {
        setTypedPh(phrase.slice(0, state.j)); state.j -= 1;
        if (state.j < 0) { state.mode = "type"; state.i = (state.i + 1) % SEARCH_PHRASES.length; state.j = 0; }
        t = setTimeout(step, 25);
      }
    };
    step(); return () => clearTimeout(t);
  }, [openSearch]);

  // lock scroll + Esc
  useEffect(() => {
    const shouldLock = openSearch || openAuth || mobileMenuOpen;
    document.documentElement.style.overflow = shouldLock ? "hidden" : "";
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (openSearch) setOpenSearch(false);
        if (openAuth) setOpenAuth(false);
        if (showAccountMenu) setShowAccountMenu(false);
        if (mobileMenuOpen) setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.documentElement.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [openSearch, openAuth, showAccountMenu, mobileMenuOpen, setOpenAuth]);

  // transparent/solid header
  useEffect(() => {
    const onScroll = () => setAtTop(allowTransparent && window.scrollY <= 10);
    onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [allowTransparent]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setAtTop(allowTransparent);
    setShowAccountMenu(false);
    setMobileMenuOpen(false);
  }, [pathname, allowTransparent]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Changed from 1024 to 768px for proper desktop/mobile split
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // announce bar fix & expose height
  useEffect(() => {
    const bar = headerRef.current?.previousElementSibling || null;
    const applyBarFixed = () => {
      if (!bar) return;
      bar.style.position = "fixed"; bar.style.top = "0"; bar.style.left = "0"; bar.style.right = "0";
      bar.style.width = "100%"; bar.style.zIndex = "70";
      const h = Math.round(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--annc", `${h}px`);
    };
    applyBarFixed();
    const ro = bar ? new ResizeObserver(applyBarFixed) : null;
    ro?.observe(bar);
    window.addEventListener("resize", applyBarFixed);
    return () => { window.removeEventListener("resize", applyBarFixed); ro?.disconnect(); };
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const query = q.trim();
    if (query) {
      setOpenSearch(false);
      // Save search to recent searches in Firestore
      if (currentUser) {
        const updatedRecent = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
        setRecentSearches(updatedRecent);
        saveUserRecentSearches(currentUser.uid, updatedRecent).catch(error => {
          console.error('Error saving recent searches:', error);
        });
      }
      navigate(`/collections?search=${encodeURIComponent(query)}`);
    }
  };
  const goQuick = (term) => {
    setQ(term);
    setOpenSearch(false);
    // Save search to recent searches in Firestore
    if (currentUser) {
      const updatedRecent = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
      setRecentSearches(updatedRecent);
      saveUserRecentSearches(currentUser.uid, updatedRecent).catch(error => {
        console.error('Error saving recent searches:', error);
      });
    }
    navigate(`/collections?search=${encodeURIComponent(term)}`);
  };

  const goGated = (e, destPath) => {
    // TEMPORARY: Allow navigation for testing - remove this after fixing auth
    // if (!isAuthed) { 
    //   e.preventDefault(); 
    //   setPendingDest(destPath); 
    //   setAuthMode("signin"); 
    //   setOpenAuth(true); 
    // }
  };

  const onAccountInvoke = () => {
    if (!isAuthed) { setPendingDest("/"); setAuthMode("signin"); setOpenAuth(true); }
    else { setShowAccountMenu((s) => !s); }
  };

  const handleSignOut = () => {
    const n = user?.name || "Guest";
    setUser(null); setShowAccountMenu(false); setMobileMenuOpen(false); navigate("/");
    setToast({ msg: `Signed out ${n}`, type: "info" });
    window.clearTimeout(handleSignOut.__t);
    handleSignOut.__t = window.setTimeout(() => setToast(null), 3200);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setShowAccountMenu(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AnnouncementBar />
      <header ref={headerRef} className={`nav-header ${atTop ? "at-top" : "solid"}`}>
        <div className="nav-row">
          {/* Mobile Menu Toggle - positioned at the start */}
          <button
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <Link to="/" className="brand" aria-label="Varaha Silks home">
            <img src={logo} alt="Varaha Silks" className="brand-logo premium-glow" loading="eager" decoding="async" fetchpriority="high" />
            <span className="brand-text" aria-label="Varaha Silks">
              <span className="brand-varaha">VARAHA</span>
              <span className="brand-silks">SILKS</span>
            </span>
          </Link>

          <nav className="nav-links" aria-label="Main" style={{ display: isMobile ? 'none' : 'flex' }}>
            <NavLink to="/" end>Home</NavLink>
            <NavLink to="/collections">Collections</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/occasion">Occasion</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </nav>

          {/* Desktop nav actions */}
          <div className="nav-actions desktop-nav-actions" style={{ display: isMobile ? 'none' : 'flex' }}>
            <div className="icon-row">
              <button type="button" className="circle-icon ringed" aria-label="Search" title="Search" onClick={() => setOpenSearch(true)}>
                <IconSearch />
              </button>

              <Link
                to="/wishlist"
                className={`circle-icon ringed ${!isAuthed ? "locked" : ""}`}
                aria-label={isAuthed ? "Wishlist" : "Sign in to view wishlist"}
                title={isAuthed ? "Wishlist" : "Sign in to view wishlist"}
                onClick={(e) => goGated(e, "/wishlist")}
              >
                <IconHeart />
                {isAuthed && wishCount > 0 && <span className="dot">{wishCount}</span>}
              </Link>

              <a href="tel:+911234567890" className="circle-icon ringed" aria-label="Call" title="Call">
                <IconPhone />
              </a>

              <button
                type="button"
                className={`circle-icon ringed ${isAuthed ? "authed" : ""} ${showAccountMenu ? "active" : ""}`}
                aria-label={isAuthed ? "Account menu" : "Sign in"}
                title={isAuthed ? "Account menu" : "Sign in"}
                onClick={onAccountInvoke}
              >
                <IconUser />
              </button>

              {isAuthed && showAccountMenu && (
                <div className="account-menu" role="menu">
                  <div className="account-greet"><span className="greet-name">Hi, {user?.name || user?.displayName || "Guest"}</span></div>
                  <Link to="/account" role="menuitem" onClick={() => setShowAccountMenu(false)}>My Account</Link>
                  <Link to="/orders" role="menuitem" onClick={() => setShowAccountMenu(false)}>My Orders</Link>
                  <button type="button" role="menuitem" onClick={handleSignOut} className="signout-btn">Sign out</button>
                  <span className="menu-caret" aria-hidden="true" />
                </div>
              )}

              <Link
                to="/cart"
                className={`circle-icon ringed ${!isAuthed ? "locked" : ""}`}
                aria-label={isAuthed ? "Cart" : "Sign in to view cart"}
                title={isAuthed ? "Cart" : "Sign in to view cart"}
                onClick={(e) => goGated(e, "/cart")}
              >
                <IconCart />
                {isAuthed && cartCount > 0 && <span className="dot">{cartCount}</span>}
              </Link>
            </div>
          </div>

          {/* Mobile search button only */}
          <div className="mobile-search-container" style={{ display: isMobile ? 'flex' : 'none' }}>
            <button type="button" className="circle-icon ringed mobile-search-btn" aria-label="Search" title="Search" onClick={() => setOpenSearch(true)}>
              <IconSearch />
            </button>
          </div>
        </div>
      </header>

      <div className={`nav-spacer ${atTop ? "" : "show"}`} aria-hidden="true" />

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-content">
          <nav className="mobile-nav-links" aria-label="Mobile navigation">
            <li>
              <NavLink to="/" end onClick={closeMobileMenu}>Home</NavLink>
            </li>
            <li>
              <NavLink to="/collections" onClick={closeMobileMenu}>Collections</NavLink>
            </li>
            <li>
              <NavLink to="/about" onClick={closeMobileMenu}>About</NavLink>
            </li>
            <li>
              <NavLink to="/occasion" onClick={closeMobileMenu}>Occasion</NavLink>
            </li>
            <li>
              <NavLink to="/contact" onClick={closeMobileMenu}>Contact</NavLink>
            </li>
          </nav>

          <div className="mobile-nav-icons">
            <button
              type="button"
              className="circle-icon ringed"
              aria-label="Search"
              onClick={() => { setOpenSearch(true); closeMobileMenu(); }}
            >
              <IconSearch />
            </button>

            <Link
              to="/wishlist"
              className={`circle-icon ringed ${!isAuthed ? "locked" : ""}`}
              aria-label={isAuthed ? "Wishlist" : "Sign in to view wishlist"}
              onClick={(e) => { goGated(e, "/wishlist"); closeMobileMenu(); }}
            >
              <IconHeart />
              {isAuthed && wishCount > 0 && <span className="dot">{wishCount}</span>}
            </Link>

            <a
              href="tel:+911234567890"
              className="circle-icon ringed"
              aria-label="Call"
              onClick={closeMobileMenu}
            >
              <IconPhone />
            </a>

            <button
              type="button"
              className={`circle-icon ringed ${isAuthed ? "authed" : ""}`}
              aria-label={isAuthed ? "Account" : "Sign in"}
              onClick={() => { onAccountInvoke(); closeMobileMenu(); }}
            >
              <IconUser />
            </button>

            <Link
              to="/cart"
              className={`circle-icon ringed ${!isAuthed ? "locked" : ""}`}
              aria-label={isAuthed ? "Cart" : "Sign in to view cart"}
              onClick={(e) => { goGated(e, "/cart"); closeMobileMenu(); }}
            >
              <IconCart />
              {isAuthed && cartCount > 0 && <span className="dot">{cartCount}</span>}
            </Link>
          </div>
        </div>
      </div>

      {/* Search sheet */}
      {openSearch && (
        <div className="search-overlay open" role="dialog" aria-modal="true" aria-label="Search">
          <button
            className="overlay-backdrop"
            aria-label="Close"
            onClick={() => setOpenSearch(false)}
          />
          <div className="search-sheet gold-border" role="document">
            <div className="aurora" aria-hidden="true" />
            <form className="sheet-bar vs-searchbar" onSubmit={onSubmit}>
              <IconSearch className="sheet-ico" />
              <input
                ref={inputRef}
                className="vs-search-input"
                type="search"
                placeholder={typedPh || "Search sarees, lehengas, kurtas"}
                aria-label="Global search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
              {q && (
                <button
                  type="button"
                  className="sheet-clear"
                  aria-label="Clear search"
                  onClick={() => setQ('')}
                  title="Clear search"
                  style={{ marginRight: '8px' }}
                >
                  <IconClose />
                </button>
              )}
              <button
                type="button"
                className="sheet-close"
                aria-label="Close search"
                onClick={() => setOpenSearch(false)}
                title="Close search"
              >
                <IconClose />
              </button>
            </form>

            <div className="sheet-body">
              {recentSearches.length > 0 && (
                <>
                  <h3 className="sheet-sub">RECENT SEARCHES</h3>
                  <ul className="trend-list">
                    {recentSearches.map((search) => (
                      <li key={search}>
                        <button
                          type="button"
                          onClick={() => goQuick(search)}
                          className="trend-item"
                        >
                          <span>{search}</span>
                          <span className="sparkle" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <h3 className="sheet-sub">POPULAR SEARCHES <IconTrend style={{ verticalAlign: "-2px" }} /></h3>
              <ul className="trend-list">
                {POPULAR_SEARCHES.map((t) => (
                  <li key={t}>
                    <button
                      type="button"
                      onClick={() => goQuick(t)}
                      className="trend-item"
                    >
                      <span>{t}</span><IconTrend />
                      <span className="sparkle" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}


      <div className={`toast ${toast ? "show" : ""} ${toast?.type || ""}`} role="status" aria-live="polite">
        <span className="toast-msg">{toast?.msg}</span>
      </div>
    </>
  );
}
